import React, { useEffect, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Journey, IntermediateStation } from '../../types';

interface JourneyMapProps {
  journey: Journey;
  className?: string;
}

const JourneyMap: React.FC<JourneyMapProps> = ({ journey, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !journey) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = 600;
    canvas.height = 400;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Set background
    ctx.fillStyle = '#f8f9fa';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate bounds
    const allPoints = [
      { lat: journey.sourceStationLatitude, lng: journey.sourceStationLongitude, name: journey.sourceStationName, type: 'source' as const },
      { lat: journey.destinationStationLatitude!, lng: journey.destinationStationLongitude!, name: journey.destinationStationName!, type: 'destination' as const },
      ...(journey.intermediateStations && Array.isArray(journey.intermediateStations) ? journey.intermediateStations.map(station => ({
        lat: station.lat,
        lng: station.lng,
        name: station.stationName,
        type: 'intermediate' as const,
        isCrossed: station.isCrossed
      })) : [])
    ];

    if (allPoints.length === 0) return;

    const minLat = Math.min(...allPoints.map(p => p.lat));
    const maxLat = Math.max(...allPoints.map(p => p.lat));
    const minLng = Math.min(...allPoints.map(p => p.lng));
    const maxLng = Math.max(...allPoints.map(p => p.lng));

    const latRange = maxLat - minLat;
    const lngRange = maxLng - minLng;
    const padding = 0.1; // 10% padding

    // Convert lat/lng to canvas coordinates
    const latToY = (lat: number) => {
      const normalizedLat = (lat - minLat) / latRange;
      return canvas.height - (normalizedLat * (canvas.height * 0.8) + canvas.height * 0.1);
    };

    const lngToX = (lng: number) => {
      const normalizedLng = (lng - minLng) / lngRange;
      return normalizedLng * (canvas.width * 0.8) + canvas.width * 0.1;
    };

    // Draw route lines
    ctx.strokeStyle = '#3498db';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw main route
    ctx.beginPath();
    ctx.moveTo(lngToX(journey.sourceStationLongitude), latToY(journey.sourceStationLatitude));
    
    // Add intermediate stations to route
    if (journey.intermediateStations && Array.isArray(journey.intermediateStations)) {
      journey.intermediateStations
        .sort((a, b) => a.sequence - b.sequence)
        .forEach(station => {
          ctx.lineTo(lngToX(station.lng), latToY(station.lat));
        });
    }
    
    ctx.lineTo(lngToX(journey.destinationStationLongitude!), latToY(journey.destinationStationLatitude!));
    ctx.stroke();

    // Draw stations
    allPoints.forEach((point, index) => {
      const x = lngToX(point.lng);
      const y = latToY(point.lat);

      // Draw station circle
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, 2 * Math.PI);
      
      switch (point.type) {
        case 'source':
          ctx.fillStyle = '#2ecc71'; // Green for source
          break;
        case 'destination':
          ctx.fillStyle = '#e74c3c'; // Red for destination
          break;
        case 'intermediate':
          if (point.isCrossed) {
            ctx.fillStyle = '#27ae60'; // Dark green for crossed
          } else {
            ctx.fillStyle = '#f39c12'; // Orange for not crossed
          }
          break;
      }
      
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw station name
      ctx.fillStyle = '#2c3e50';
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      
      // Position text above or below the point to avoid overlap
      const textY = y > canvas.height / 2 ? y - 15 : y + 20;
      ctx.fillText(point.name, x, textY);
    });

    // Draw current location if available
    if (journey.currentLatitude && journey.currentLongitude) {
      const currentX = lngToX(journey.currentLongitude);
      const currentY = latToY(journey.currentLatitude);

      // Draw current location with pulsing effect
      ctx.beginPath();
      ctx.arc(currentX, currentY, 12, 0, 2 * Math.PI);
      ctx.fillStyle = '#3498db';
      ctx.fill();
      
      // Draw pulsing circle
      ctx.beginPath();
      ctx.arc(currentX, currentY, 20, 0, 2 * Math.PI);
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw "You are here" text
      ctx.fillStyle = '#3498db';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('You are here', currentX, currentY - 25);
    }

    // Draw legend
    const legendY = canvas.height - 20;
    const legendItems = [
      { color: '#2ecc71', label: 'Source' },
      { color: '#f39c12', label: 'Intermediate' },
      { color: '#27ae60', label: 'Crossed' },
      { color: '#e74c3c', label: 'Destination' },
      { color: '#3498db', label: 'Current Location' }
    ];

    legendItems.forEach((item, index) => {
      const x = 20 + index * 120;
      
      // Draw legend circle
      ctx.beginPath();
      ctx.arc(x, legendY, 6, 0, 2 * Math.PI);
      ctx.fillStyle = item.color;
      ctx.fill();
      
      // Draw legend text
      ctx.fillStyle = '#2c3e50';
      ctx.font = '12px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(item.label, x + 10, legendY + 4);
    });

  }, [journey]);

  if (!journey) {
    return (
      <Paper className={className} sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          No journey data available
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={className} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Journey Route Map
      </Typography>
      <Box sx={{ overflow: 'auto', textAlign: 'center' }}>
        <canvas
          ref={canvasRef}
          style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            maxWidth: '100%',
            height: 'auto'
          }}
        />
      </Box>
    </Paper>
  );
};

export default JourneyMap;
